// Formatting + wire-value helpers local to the sales-invoicing feature (S0
// ownership: this goal touches only apps/web/src/routes/_authenticated/
// sales-invoicing* and apps/web/src/features/sales-invoicing/). Duplicated from
// the equivalent helpers in features/purchasing-intake/common.ts and features/
// stores-transfers/format.ts rather than imported cross-feature — same precedent
// stores-transfers/api.ts's header documents for its own lookup hooks ("no shared
// web lookups file pre-created in plan.md's S0 registry ... duplicating a few
// small hooks here keeps this goal's diff inside its own registry").

/** ZAR cents (bigint or its wire decimal-string form) -> "R1 234,56" (en-ZA:
 * space-thousands, comma-decimal — SA convention, not US). Grouping goes
 * through `Intl.NumberFormat('en-ZA').format(rand)` on the bigint whole-rand
 * value directly (its `.format` overload accepts `bigint`, no `Number(cents)`
 * conversion), so precision is exact even for amounts beyond
 * Number.MAX_SAFE_INTEGER; the cents remainder is likewise derived from the
 * bigint's own `% 100n`, never floating point. */
export function formatMoneyCents(cents: bigint | string | number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  const value = typeof cents === 'bigint' ? cents : BigInt(Math.trunc(Number(cents)));
  const negative = value < 0n;
  const abs = negative ? -value : value;
  const rand = abs / 100n;
  const remainder = (abs % 100n).toString().padStart(2, '0');
  const grouped = new Intl.NumberFormat('en-ZA').format(rand);
  return `${negative ? '-' : ''}R${grouped},${remainder}`;
}

/** A float cents estimate (e.g. qty x avg cost) -> "R1,234.56" — used only for
 * client-side ESTIMATE previews (never round-tripped to the wire). */
export function formatRands(cents: number): string {
  const negative = cents < 0;
  const rands = Math.abs(cents) / 100;
  const formatted = rands.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${negative ? '-' : ''}R${formatted}`;
}

/** A decimal-string quantity (kg, tonnes) -> a grouped display string. */
export function formatQty(value: string | number | null | undefined, decimals = 3): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString('en-ZA', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/** User-typed Rand amount (e.g. "1234.56") -> integer ZAR cents as a decimal
 * string, for wire bodies that use `moneyCentsSchema`. Deterministic string/BigInt
 * math (never `Math.round(x * 100)`) so entering a price never drifts a cent to
 * floating-point error. */
export function randToCents(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '0';
  const negative = trimmed.startsWith('-');
  const abs = negative ? trimmed.slice(1) : trimmed;
  const [wholePart, fracPart = ''] = abs.split('.');
  const frac = `${fracPart}00`.slice(0, 2);
  const digits = `${wholePart || '0'}${frac}`.replace(/^0+(?=\d)/, '');
  const cents = BigInt(digits || '0');
  return (negative ? -cents : cents).toString();
}

/** Inverse of `randToCents` — integer ZAR cents (wire decimal string) -> a plain
 * Rand string for a form input, e.g. "1234.56". */
export function centsToRand(cents: string | null | undefined): string {
  if (cents == null || cents === '') return '';
  const value = BigInt(cents);
  const negative = value < 0n;
  const abs = negative ? -value : value;
  const rand = abs / 100n;
  const remainder = (abs % 100n).toString().padStart(2, '0');
  return `${negative ? '-' : ''}${rand.toString()}.${remainder}`;
}

/** Cents-per-TONNE (integer ZAR cents, wire decimal string) -> a Rand-per-BASE-UOM
 * ("per kg") string, rounded half-up to the nearest whole cent (1 tonne = 1000 kg
 * exactly; `moneyCentsSchema` allows no finer precision than a whole cent per unit
 * on the dispatch line itself). This is only ever used to PREFILL the dispatch
 * wizard's editable price field from a customer contract's per-tonne rate — the
 * user reviews/adjusts it before Post, so half-up rounding here is a reasonable
 * default, not the ledger of record. */
export function tonneCentsToUnitRand(priceCentsPerTonne: string): string {
  const cents = BigInt(priceCentsPerTonne);
  const negative = cents < 0n;
  const abs = negative ? -cents : cents;
  const unitCents = (abs + 500n) / 1000n;
  return centsToRand((negative ? -unitCents : unitCents).toString());
}

/** `avgCostCentsPerUnit` (a decimal-string, 4dp) x `qty` (a decimal-string, 3dp) as
 * a display-only cents ESTIMATE. NOT the authoritative value — the real ledger
 * effect (and margin) is only known once `recordMovement` runs at Post time (WAC
 * can move between preview and post), which is why every screen using this labels
 * the number "Estimated". */
export function estimateValueCents(avgCostCentsPerUnit: string | undefined, qty: string): number | null {
  if (avgCostCentsPerUnit === undefined) return null;
  const avg = Number(avgCostCentsPerUnit);
  const q = Number(qty);
  if (!Number.isFinite(avg) || !Number.isFinite(q)) return null;
  return avg * q;
}

/** Unit-aware label for a quantity input that captures one product line — a
 * sales order line can be a mass (KG) product or a count (EACH) product
 * (BUILD_SPEC.md §5.2 base_uom KG|EACH|L), so the field asking for its
 * quantity must say which. Mirrors production-packconv/format.ts's identical
 * `qtyInputLabel` helper (this feature keeps its own tiny copy rather than a
 * cross-feature import, per the S0 convention documented on that file: each
 * web goal owns its own files). `baseUom` is typed `string | undefined` here
 * (not `@zebra/core`'s `BaseUom`) to match this feature's own `ProductRow.
 * baseUom: string` (types.ts) rather than tightening a type outside this
 * goal's scope. */
export function qtyInputLabel(baseUom: string | undefined, kgLabel: string, eachLabel: string): string {
  return baseUom === 'EACH' ? eachLabel : kgLabel;
}

/** Unit-aware per-row quantity display for a table that can mix KG- and
 * EACH-denominated lines within the same document (a sales order's lines are
 * not all one product) — same baseUom scoping as `qtyInputLabel`, applied per
 * row instead of to a single table-wide header. EACH renders as a whole count
 * with no decimals ("counted not weighed", per base_uom semantics); KG keeps
 * the existing 3dp formatting. Mirrors production-packconv/format.ts's
 * identical `formatLineQty` helper. */
export function formatLineQty(qty: string | number | null | undefined, baseUom: string | undefined): string {
  if (baseUom === 'EACH') {
    if (qty === null || qty === undefined || qty === '') return '—';
    const n = Number(qty);
    return Number.isFinite(n) ? `${n.toLocaleString('en-ZA', { maximumFractionDigits: 0 })} units` : '—';
  }
  return `${formatQty(qty)} kg`;
}

export function formatDate(value: Date | string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatDateTime(value: Date | string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** `datetime-local` input default: "now", truncated to the minute. */
export function nowForDatetimeLocal(): string {
  const d = new Date();
  d.setSeconds(0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// NOTE: `todayIsoDate` (the SAST "today" default) moved to the shared
// `src/lib/sastDate.ts` (spec §4 item 7, Amendment 2026-07-10) — SAST business-date
// helpers are app-wide concerns, not per-feature formatters, and a third duplicated
// copy (stores-transfers had its own) was drift waiting to happen.

function toMilliKg(value: string): bigint {
  const trimmed = value.trim();
  if (!trimmed) return 0n;
  const negative = trimmed.startsWith('-');
  const abs = negative ? trimmed.slice(1) : trimmed;
  const [whole, frac = ''] = abs.split('.');
  const paddedFrac = `${frac}000`.slice(0, 3);
  const milli = BigInt(`${whole || '0'}${paddedFrac}`);
  return negative ? -milli : milli;
}

function fromMilliKg(value: bigint): string {
  const negative = value < 0n;
  const abs = negative ? -value : value;
  const whole = abs / 1000n;
  const frac = (abs % 1000n).toString().padStart(3, '0');
  return `${negative ? '-' : ''}${whole.toString()}.${frac}`;
}

/** Exact decimal-string subtraction for weighbridge kg readings (gross - tare),
 * scaled through integer milli-kg so the wizard's live "net" preview never drifts
 * off a gram to floating-point error before the server computes the authoritative
 * `netKg` itself. */
export function subtractKg(grossKg: string, tareKg: string): string {
  return fromMilliKg(toMilliKg(grossKg || '0') - toMilliKg(tareKg || '0'));
}
